import React, { useState, useContext, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronDown, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import '../../utils/agGridSetup';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import DashboardLayout from '../../components/DashboardLayout';
import SuccessModal from '../../components/modals/SuccessModal';
import '../../styles/pages/alerts.css';
import '../../styles/pages/user-management.css';
import '../../styles/pages/DeviceTableView.css';
import { UserManagementContext } from '../../contexts/UserManagementContext';
import { CreateUserRequestDto, UpdateUserRequestDto, UserDto } from '../../services/userManagementApi';
import { roleMap } from '../../utils/helperFunctions';
import { getUserTimezone } from '../../utils/timezoneUtils';

const AddUserPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const userId = id ? parseInt(id, 10) : null;

  const LoggedInUserData = JSON.parse(localStorage.getItem('userData') || '{}');
  const ROLES: UserDto['role'][] = roleMap[LoggedInUserData.role] || [];

  // Form state
  const [firstName, setFirstName]     = useState('');
  const [email, setEmail]             = useState('');
  const [role, setRole]               = useState<UserDto['role'] | ''>('');
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [errors, setErrors]           = useState<{ firstName?: string; email?: string; role?: string }>({});

  // Site access
  const [selectedSiteIds, setSelectedSiteIds] = useState<Set<number>>(new Set());
  const [siteSearch, setSiteSearch]           = useState('');

  // Feedback modal
  const [feedbackModal, setFeedbackModal] = useState<{
    isOpen: boolean; status: 'success' | 'failed'; title: string; subtitle?: string;
  }>({ isOpen: false, status: 'success', title: '' });

  const userContext = useContext(UserManagementContext);
  if (!userContext) throw new Error('UserManagementContext missing');

  const { createUser, updateUser, Users, sites, getSites, error } = userContext;

  const gridRef = useRef<AgGridReact>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages]   = useState(0);

  const goToPage = (page: number) => {
    if (gridRef.current?.api) {
      gridRef.current.api.paginationGoToPage(page);
      setCurrentPage(page);
    }
  };

  useEffect(() => { getSites(); }, []);

  useEffect(() => {
    if (!isEditMode || !userId) return;
    const user = Users.find((u) => u.userId === userId);
    if (user) {
      setFirstName(user.firstName);
      setEmail(user.email);
      setRole(user.role);
      const ids = Array.isArray(user.accessSites) ? user.accessSites.map((site) => site.siteId) : [];
      setSelectedSiteIds(new Set(ids));
    }
  }, [userId]);

  // All site rows
  const allSiteRows = useMemo(() => {
    return sites.map((s) => ({ id: s.siteId, name: s.siteName ?? `Site ${s.siteId}`, location: s.siteLocation ?? '' }));
  }, [sites]);

  // Filtered rows for grid
  const filteredRows = useMemo(
    () => allSiteRows.filter((r) => r.name.toLowerCase().includes(siteSearch.toLowerCase())),
    [allSiteRows, siteSearch]
  );

  const allSelected = filteredRows.length > 0 && filteredRows.every((r) => selectedSiteIds.has(r.id));

  const rowData = useMemo(
    () => filteredRows.map((r, i) => ({ sno: i + 1, id: r.id, name: r.name, location: r.location, selected: selectedSiteIds.has(r.id) })),
    [filteredRows, selectedSiteIds]
  );

  const toggleSite = useCallback((id: number) => {
    setSelectedSiteIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedSiteIds((prev) => {
      const next = new Set(prev);
      const all = filteredRows.every((r) => next.has(r.id));
      if (all) filteredRows.forEach((r) => next.delete(r.id));
      else filteredRows.forEach((r) => next.add(r.id));
      return next;
    });
  }, [filteredRows]);

  // Column definitions — rebuild when selection or filtered list changes so header checkbox stays in sync
  const columnDefs: ColDef[] = useMemo(() => [
    {
      headerName: 'S.No.',
      field: 'sno',
      flex: 1,
      sortable: false,
      filter: false,
      resizable: false,
    },
    {
      headerName: 'Site Name',
      field: 'name',
      flex: 1,
      sortable: false,
      filter: false,
      resizable: false,
    },
    {
      headerName: 'Site Location',
      field: 'location',
      flex: 1,
      sortable: false,
      filter: false,
      resizable: false,
      valueGetter: (params: any) => params.data?.location || '-',
    },
    {
      field: 'selected',
      flex: 1,
      sortable: false,
      filter: false,
      resizable: false,
      headerComponent: () => (
        <div className="aup-all-access-header">
          <input
            type="checkbox"
            className="aup-checkbox"
            checked={allSelected}
            onChange={toggleAll}
          />
          <span>All Access</span>
        </div>
      ),
      cellRenderer: (params: any) => {
        console.log(params.data);
        return (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <input
            type="checkbox"
            className="aup-checkbox"
            checked={params.data.selected}
            onChange={() => toggleSite(params.data.id)}
          />
        </div>
      )},
    },
  ], [allSelected, toggleAll, toggleSite]);

  // Validation & submit
  const validate = () => {
    const newErrors: typeof errors = {};
    if (!firstName.trim()) {
      newErrors.firstName = 'Name is required.';
    } else if (firstName.trim().length < 2) {
      newErrors.firstName = 'Name must be at least 2 characters.';
    } else if (firstName.length > 25) {
      newErrors.firstName = 'Name cannot exceed 25 characters.';
    } else if (!/^[a-zA-Z\s]+$/.test(firstName)) {
      newErrors.firstName = 'Name can only contain letters and spaces.';
    } else if (/^([a-zA-Z])\1+$/.test(firstName.trim())) {
      newErrors.firstName = 'Enter a valid name.';
    } else if (/([a-zA-Z])\1{2,}/.test(firstName)) {
      newErrors.firstName = 'Name cannot have a character repeated more than twice in a row.';
    }
    if (!email.trim()) newErrors.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = 'Enter a valid email.';
    if (!role) newErrors.role = 'Please select a role.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    if (isEditMode && userId) {
      const siteIds = Array.from(selectedSiteIds);
      const payload: UpdateUserRequestDto = { firstName, email, role: role as UserDto['role'], siteIds };
      const success = await updateUser(userId, payload);
      setFeedbackModal(success
        ? { isOpen: true, status: 'success', title: 'User Updated!', subtitle: 'The user details have been updated successfully.' }
        : { isOpen: true, status: 'failed', title: 'Failed to Update User', subtitle: error || 'Something went wrong.' });
    } else {
      const siteIds = Array.from(selectedSiteIds);
      const payload: CreateUserRequestDto = { firstName, email, password: 'User@123', role: role as UserDto['role'], loginType: 'User Credentials', timezone: getUserTimezone(), siteIds };
      const result = await createUser(payload);
      setFeedbackModal(result?.success
        ? { isOpen: true, status: 'success', title: 'User Created!', subtitle: result.message || 'The new user has been added successfully.' }
        : { isOpen: true, status: 'failed', title: 'Failed to Create User', subtitle: result?.message || error || 'Something went wrong.' });
    }
  };

  const handleFeedbackClose = () => {
    setFeedbackModal((prev) => ({ ...prev, isOpen: false }));
    if (feedbackModal.status === 'success') navigate('/user-management');
  };

  return (
    <DashboardLayout>
      <div className="aup-page-container">  
        <div className='aup-page-wrap zoomOutLevel'>
          {/* ── Header ─────────────────────────────────────────────── */}
          <div className="alerts-page-header">
            <div className="alerts-page-header-left">
              <div className="aup-title-with-icon">
                <button className="back-button zoomOutLevelForDeviceInfoCard" onClick={() => navigate('/user-management')}>
                  <svg viewBox="0 0 24 24" fill="currentColor" className="back-icon">
                    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                  </svg>
                </button>
                <h2 className="device-title">{isEditMode ? 'Edit User' : 'Add New User'}</h2>
              </div>
              <p className="alerts-subtitle device-subtitle ml-12">
                {isEditMode ? 'Update the user account details and access.' : 'Create a new user account and assign them to a group.'}
              </p>
            </div>
          </div>

          <>
              {/* ── User Details ─────────────────────────────────────── */}
              <div className="aup-section">
                <p className="na-section-label">USER DETAILS</p>
                <div className="aup-form-row">

                  <div className="form-group">
                    <label className="form-label">Name</label>
                    <input type="text" className="form-input" placeholder="Enter Name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                    {errors.firstName && <p className="aup-error">{errors.firstName}</p>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input type="email" className="form-input" placeholder="Enter Email" value={email} onChange={(e) => setEmail(e.target.value)} />
                    {errors.email && <p className="aup-error">{errors.email}</p>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Role</label>
                    <div style={{ position: 'relative' }}>
                      <button type="button" className={`form-dropdown-button ${showRoleDropdown ? 'active' : ''}`} onClick={() => setShowRoleDropdown((p) => !p)}>
                        <span style={{ color: role ? 'var(--color-text)' : undefined }}>{role || 'Select Role'}</span>
                        <ChevronDown size={18} />
                      </button>
                      {showRoleDropdown && (
                        <div className="dropdown-menu">
                          {ROLES.map((r) => (
                            <div key={r} className="dropdown-item" onClick={() => { setRole(r); setShowRoleDropdown(false); }}>{r}</div>
                          ))}
                        </div>
                      )}
                    </div>
                    {errors.role && <p className="aup-error">{errors.role}</p>}
                  </div>

                </div>
              </div>

              {/* ── Site Access Table ────────────────────────────────── */}
              <div className="aup-access-section">
                <div className="aup-access-header">
                  <p className="na-section-label" style={{ margin: 0 }}>SITE ACCESS</p>
                  <div className="aup-search-wrap">
                    <Search size={14} className="aup-search-icon" />
                    <input
                      className="aup-search-input"
                      placeholder="Search by site name"
                      value={siteSearch}
                      onChange={(e) => setSiteSearch(e.target.value)}
                    />
                  </div>
                </div>

                <div className="ag-theme-alpine w-full device-view-container">
                  <AgGridReact
                    ref={gridRef}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    defaultColDef={{ sortable: false, filter: false, resizable: false }}
                    suppressCellFocus={true}
                    domLayout="normal"
                    rowHeight={60}
                    headerHeight={60}
                    pagination={true}
                    paginationPageSize={5}
                    suppressPaginationPanel={true}
                    className="device-table-grid"
                    overlayNoRowsTemplate='<span class="custom-no-rows">No sites found</span>'
                    onPaginationChanged={() => {
                      if (gridRef.current?.api) {
                        setCurrentPage(gridRef.current.api.paginationGetCurrentPage());
                        setTotalPages(gridRef.current.api.paginationGetTotalPages());
                      }
                    }}
                  />
                
                  {/* Pagination Panel */}
                  {totalPages > 1 && (
                    <div className="pagination-container h-auto">
                      <button
                        disabled={currentPage <= 0}
                        className="pagination-button pagination-previous"
                        onClick={() => goToPage(currentPage - 1)}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>

                      {Array.from({ length: totalPages }, (_, i) => (
                        <button
                          key={i}
                          onClick={() => goToPage(i)}
                          className={`pagination-button ${currentPage === i ? 'active' : ''}`}
                        >
                          {i + 1}
                        </button>
                      ))}

                      <button
                        disabled={currentPage === totalPages - 1}
                        className="pagination-button pagination-next"
                        onClick={() => goToPage(currentPage + 1)}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* ── Footer ──────────────────────────────────────────── */}
              <div className="aup-footer">
                <button className="aup-btn aup-btn-secondary" onClick={() => navigate('/user-management')}>Cancel</button>
                <button className="aup-btn aup-btn-primary" onClick={handleSubmit}>
                  {isEditMode ? 'Update User' : 'Create User'}
                </button>
              </div>
          </>
        </div>

      </div>

      <SuccessModal
        isOpen={feedbackModal.isOpen}
        onClose={handleFeedbackClose}
        status={feedbackModal.status}
        title={feedbackModal.title}
        subtitle={feedbackModal.subtitle}
      />
    </DashboardLayout>
  );
};

export default AddUserPage;
